export interface EmailTemplate {
  id: string
  name: string
  subject: string
  content: string
  category: 'welcome' | 'notification' | 'marketing' | 'system' | 'support'
  variables: string[]
}

export interface EmailRecipient {
  email: string
  name: string
  [key: string]: any
}

export interface EmailOptions {
  to: EmailRecipient[]
  subject: string
  content: string
  template?: EmailTemplate
  variables?: Record<string, any>
  priority?: 'low' | 'normal' | 'high'
  sendAt?: Date
}

export interface EmailStats {
  sentToday: number
  todayIncrease: number
  deliveryRate: number
  openRate: number
}

class EmailService {
  private baseURL = '/api/email' // This would be your backend API endpoint

  /**
   * Send an email
   */
  async sendEmail(options: EmailOptions): Promise<{ success: boolean; messageId?: string; error?: string }> {
    try {
      // Process template variables if template is provided
      let processedContent = options.content
      let processedSubject = options.subject

      if (options.template && options.variables) {
        processedContent = this.processTemplate(options.template.content, options.variables)
        processedSubject = this.processTemplate(options.template.subject, options.variables)
      } else if (options.variables) {
        processedContent = this.processTemplate(options.content, options.variables)
        processedSubject = this.processTemplate(options.subject, options.variables)
      }

      // For now, simulate API call
      // TODO: Replace with actual API call to your email service
      console.log('Sending email:', {
        to: options.to,
        subject: processedSubject,
        content: processedContent,
        priority: options.priority || 'normal',
        sendAt: options.sendAt
      })

      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Simulate success (95% success rate)
      const success = Math.random() > 0.05

      if (success) {
        return {
          success: true,
          messageId: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        }
      } else {
        return {
          success: false,
          error: 'Failed to send email. Please try again.'
        }
      }

    } catch (error) {
      console.error('Email service error:', error)
      return {
        success: false,
        error: 'Email service unavailable'
      }
    }
  }

  /**
   * Send welcome email to new user
   */
  async sendWelcomeEmail(user: { email: string; name: string; id: string }): Promise<boolean> {
    const welcomeTemplate = await this.getTemplate('welcome-new-user')
    
    if (!welcomeTemplate) {
      // Fallback to default welcome email
      const result = await this.sendEmail({
        to: [{ email: user.email, name: user.name }],
        subject: 'Welcome to OilBuddy AI!',
        content: this.getDefaultWelcomeContent(),
        variables: {
          'user.name': user.name,
          'user.email': user.email,
          'user.id': user.id,
          'company.name': 'OilBuddy AI',
          'support.email': 'support@oilbuddy.ai'
        },
        priority: 'high'
      })
      
      return result.success
    }

    const result = await this.sendEmail({
      to: [{ email: user.email, name: user.name }],
      subject: welcomeTemplate.subject,
      content: welcomeTemplate.content,
      template: welcomeTemplate,
      variables: {
        'user.name': user.name,
        'user.email': user.email,
        'user.id': user.id,
        'company.name': 'OilBuddy AI',
        'support.email': 'support@oilbuddy.ai',
        'dashboard.url': `${window.location.origin}/dashboard`,
        'profile.url': `${window.location.origin}/profile`
      },
      priority: 'high'
    })

    return result.success
  }

  /**
   * Send password reset email
   */
  async sendPasswordResetEmail(user: { email: string; name: string }, resetToken: string): Promise<boolean> {
    const resetUrl = `${window.location.origin}/reset-password?token=${resetToken}`
    
    const result = await this.sendEmail({
      to: [{ email: user.email, name: user.name }],
      subject: 'Reset Your OilBuddy AI Password',
      content: this.getPasswordResetContent(),
      variables: {
        'user.name': user.name,
        'reset.url': resetUrl,
        'company.name': 'OilBuddy AI',
        'support.email': 'support@oilbuddy.ai'
      },
      priority: 'high'
    })

    return result.success
  }

  /**
   * Send notification email
   */
  async sendNotificationEmail(
    users: EmailRecipient[],
    subject: string,
    message: string,
    type: 'info' | 'warning' | 'success' | 'error' = 'info'
  ): Promise<boolean> {
    const result = await this.sendEmail({
      to: users,
      subject: `[${type.toUpperCase()}] ${subject}`,
      content: this.getNotificationContent(message, type),
      variables: {
        'company.name': 'OilBuddy AI',
        'support.email': 'support@oilbuddy.ai',
        'notification.type': type,
        'notification.message': message
      },
      priority: type === 'error' || type === 'warning' ? 'high' : 'normal'
    })

    return result.success
  }

  /**
   * Get email statistics
   */
  async getEmailStats(): Promise<EmailStats> {
    try {
      // TODO: Replace with actual API call
      // const response = await fetch(`${this.baseURL}/stats`)
      // return await response.json()

      // Simulate stats for now
      return {
        sentToday: Math.floor(Math.random() * 100) + 20,
        todayIncrease: Math.floor(Math.random() * 30) + 5,
        deliveryRate: 98.5,
        openRate: 65.2
      }
    } catch (error) {
      console.error('Failed to fetch email stats:', error)
      return {
        sentToday: 0,
        todayIncrease: 0,
        deliveryRate: 0,
        openRate: 0
      }
    }
  }

  /**
   * Get email templates
   */
  async getTemplates(): Promise<EmailTemplate[]> {
    try {
      // TODO: Replace with actual API call
      // const response = await fetch(`${this.baseURL}/templates`)
      // return await response.json()

      // Return default templates for now
      return this.getDefaultTemplates()
    } catch (error) {
      console.error('Failed to fetch email templates:', error)
      return []
    }
  }

  /**
   * Get specific template by ID
   */
  async getTemplate(id: string): Promise<EmailTemplate | null> {
    const templates = await this.getTemplates()
    return templates.find(t => t.id === id) || null
  }

  /**
   * Process template variables
   */
  private processTemplate(content: string, variables: Record<string, any>): string {
    let processed = content

    // Replace variables in the format {{variable.name}}
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`{{\\s*${key.replace('.', '\\.')}\\s*}}`, 'g')
      processed = processed.replace(regex, String(value))
    })

    return processed
  }

  /**
   * Get default welcome email content
   */
  private getDefaultWelcomeContent(): string {
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #10b981; margin-bottom: 10px;">Welcome to {{company.name}}!</h1>
          <p style="color: #6b7280; font-size: 16px;">Hi {{user.name}}, we're excited to have you on board!</p>
        </div>
        
        <div style="background: #f9fafb; border-radius: 8px; padding: 20px; margin-bottom: 30px;">
          <h2 style="color: #374151; margin-bottom: 15px;">Get Started</h2>
          <p style="color: #6b7280; line-height: 1.6; margin-bottom: 15px;">
            Your account has been successfully created. You can now access all the features of our platform:
          </p>
          <ul style="color: #6b7280; line-height: 1.8;">
            <li>Browse and apply for oil & gas jobs</li>
            <li>Manage your profile and certifications</li>
            <li>Track your applications and schedules</li>
            <li>Connect with AI-powered job matching</li>
          </ul>
        </div>
        
        <div style="text-align: center; margin-bottom: 30px;">
          <a href="{{dashboard.url}}" style="background: linear-gradient(to right, #10b981, #06b6d4); color: white; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; display: inline-block;">
            Access Your Dashboard
          </a>
        </div>
        
        <div style="border-top: 1px solid #e5e7eb; padding-top: 20px; text-align: center;">
          <p style="color: #9ca3af; font-size: 14px;">
            Need help? Contact us at <a href="mailto:{{support.email}}" style="color: #10b981;">{{support.email}}</a>
          </p>
        </div>
      </div>
    `
  }

  /**
   * Get password reset email content
   */
  private getPasswordResetContent(): string {
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #dc2626; margin-bottom: 10px;">Password Reset Request</h1>
          <p style="color: #6b7280; font-size: 16px;">Hi {{user.name}}, we received a request to reset your password.</p>
        </div>
        
        <div style="background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 20px; margin-bottom: 30px;">
          <h2 style="color: #dc2626; margin-bottom: 15px;">Reset Your Password</h2>
          <p style="color: #6b7280; line-height: 1.6; margin-bottom: 15px;">
            Click the button below to reset your password. This link will expire in 1 hour for security reasons.
          </p>
        </div>
        
        <div style="text-align: center; margin-bottom: 30px;">
          <a href="{{reset.url}}" style="background: #dc2626; color: white; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; display: inline-block;">
            Reset Password
          </a>
        </div>
        
        <div style="background: #f3f4f6; border-radius: 8px; padding: 15px; margin-bottom: 20px;">
          <p style="color: #6b7280; font-size: 14px; margin: 0;">
            <strong>Didn't request this?</strong> You can safely ignore this email. Your password won't be changed.
          </p>
        </div>
        
        <div style="border-top: 1px solid #e5e7eb; padding-top: 20px; text-align: center;">
          <p style="color: #9ca3af; font-size: 14px;">
            Need help? Contact us at <a href="mailto:{{support.email}}" style="color: #10b981;">{{support.email}}</a>
          </p>
        </div>
      </div>
    `
  }

  /**
   * Get notification email content
   */
  private getNotificationContent(message: string, type: string): string {
    const colors = {
      info: { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af' },
      success: { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534' },
      warning: { bg: '#fffbeb', border: '#fed7aa', text: '#d97706' },
      error: { bg: '#fef2f2', border: '#fecaca', text: '#dc2626' }
    }

    const color = colors[type as keyof typeof colors] || colors.info

    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: ${color.text}; margin-bottom: 10px;">{{company.name}} Notification</h1>
        </div>
        
        <div style="background: ${color.bg}; border: 1px solid ${color.border}; border-radius: 8px; padding: 20px; margin-bottom: 30px;">
          <div style="color: ${color.text}; line-height: 1.6;">
            {{notification.message}}
          </div>
        </div>
        
        <div style="border-top: 1px solid #e5e7eb; padding-top: 20px; text-align: center;">
          <p style="color: #9ca3af; font-size: 14px;">
            Need help? Contact us at <a href="mailto:{{support.email}}" style="color: #10b981;">{{support.email}}</a>
          </p>
        </div>
      </div>
    `
  }

  /**
   * Get default email templates
   */
  private getDefaultTemplates(): EmailTemplate[] {
    return [
      {
        id: 'welcome-new-user',
        name: 'Welcome New User',
        subject: 'Welcome to {{company.name}}!',
        content: this.getDefaultWelcomeContent(),
        category: 'welcome',
        variables: ['user.name', 'user.email', 'company.name', 'dashboard.url', 'support.email']
      },
      {
        id: 'password-reset',
        name: 'Password Reset',
        subject: 'Reset Your Password',
        content: this.getPasswordResetContent(),
        category: 'system',
        variables: ['user.name', 'reset.url', 'company.name', 'support.email']
      },
      {
        id: 'job-application-received',
        name: 'Job Application Received',
        subject: 'Application Received - {{job.title}}',
        content: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <h1 style="color: #10b981;">Application Received</h1>
            <p>Hi {{user.name}},</p>
            <p>We've received your application for the position of <strong>{{job.title}}</strong> at {{company.name}}.</p>
            <p>We'll review your application and get back to you soon.</p>
            <p>Best regards,<br>The {{company.name}} Team</p>
          </div>
        `,
        category: 'notification',
        variables: ['user.name', 'job.title', 'company.name']
      },
      {
        id: 'safety-alert',
        name: 'Safety Alert',
        subject: '[SAFETY ALERT] {{alert.title}}',
        content: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 20px;">
              <h1 style="color: #dc2626;">Safety Alert</h1>
              <p><strong>{{alert.title}}</strong></p>
              <p>{{alert.description}}</p>
              <p>Please take immediate action as required.</p>
            </div>
          </div>
        `,
        category: 'system',
        variables: ['alert.title', 'alert.description', 'company.name']
      }
    ]
  }
}

// Export singleton instance
export const emailService = new EmailService()
export default emailService
